import { Stack, type Href } from 'expo-router';
import { CalendarIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';

import { MemberEventFeedRow } from '@/components/member-event-feed-row';
import { NavigationBackToolbar } from '@/components/navigation-back-toolbar';
import { ProfileScreen } from '@/components/profiles/profile-screen';
import { Text } from '@sixam/ui/text';
import { fetchPublishedEvents, type Event } from '@/lib/events';
import { fetchTenantBySlug, type Tenant } from '@/lib/tenant';

type TenantProfileScreenProps = Readonly<{
  backFallbackHref: Href;
  eventHref: (tenant: Tenant, event: Event) => Href;
  eventsHref: (tenant: Tenant) => Href;
  tenantSlug: string;
}>;

function displayNameForTenant(tenant: Tenant | null) {
  return tenant?.name || 'Club profile';
}

function UpcomingEvents({
  error,
  eventHref,
  events,
  tenant,
}: Readonly<{
  error: string | null;
  eventHref: (tenant: Tenant, event: Event) => Href;
  events: Event[];
  tenant: Tenant;
}>) {
  return (
    <>
      <Text variant="large">Upcoming</Text>
      {error ? (
        <Text className="text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}
      {!error && events.length === 0 ? (
        <Text variant="muted">No public events right now. Check back soon.</Text>
      ) : null}
      {events.length > 0 ? (
        <View className="-mx-4">
          {events.slice(0, 3).map((event) => (
            <MemberEventFeedRow
              key={event.id}
              clubName={tenant.name}
              clubSlug={tenant.slug}
              event={event}
              href={eventHref(tenant, event)}
              timeZone={tenant.timezone}
            />
          ))}
        </View>
      ) : null}
    </>
  );
}

export function PublicTenantProfileScreen({
  backFallbackHref,
  eventHref,
  eventsHref,
  tenantSlug,
}: TenantProfileScreenProps) {
  const [tenant, setTenant] = React.useState<Tenant | null>(null);
  const [events, setEvents] = React.useState<Event[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [eventsError, setEventsError] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;

    async function load() {
      setIsLoading(true);
      try {
        const data = await fetchTenantBySlug(tenantSlug);
        let nextEvents: Event[] = [];
        let nextEventsError: string | null = null;

        if (data) {
          try {
            nextEvents = await fetchPublishedEvents(data.id);
          } catch (loadEventsError) {
            nextEventsError =
              loadEventsError instanceof Error ? loadEventsError.message : 'Failed to load events';
          }
        }

        if (active) {
          setTenant(data);
          setEvents(nextEvents);
          setEventsError(nextEventsError);
          setError(null);
        }
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load club profile.');
          setTenant(null);
          setEvents([]);
          setEventsError(null);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      active = false;
    };
  }, [tenantSlug]);

  if (isLoading) {
    return (
      <>
        <Stack.Screen options={{ headerBackVisible: false, headerShown: true, title: 'Club' }} />
        <NavigationBackToolbar fallbackHref={backFallbackHref} />
        <ScrollView
          className="flex-1 bg-background"
          contentContainerClassName="items-center justify-center p-6"
          contentInsetAdjustmentBehavior="automatic"
        >
          <ActivityIndicator />
        </ScrollView>
      </>
    );
  }

  if (error || !tenant) {
    return (
      <>
        <Stack.Screen options={{ headerBackVisible: false, headerShown: true, title: 'Club' }} />
        <NavigationBackToolbar fallbackHref={backFallbackHref} />
        <ScrollView
          className="flex-1 bg-background"
          contentContainerClassName="gap-4 p-6"
          contentInsetAdjustmentBehavior="automatic"
        >
          <Text variant="h2">Club profile</Text>
          <Text className="text-destructive" selectable>
            {error ?? 'This club is not available.'}
          </Text>
        </ScrollView>
      </>
    );
  }

  const displayName = displayNameForTenant(tenant);

  return (
    <>
      <Stack.Screen options={{ headerBackVisible: false, headerShown: true, title: displayName }} />
      <NavigationBackToolbar fallbackHref={backFallbackHref} />
      <ProfileScreen
        actions={[
          {
            href: eventsHref(tenant),
            icon: CalendarIcon,
            key: 'events',
            label: 'Events',
          },
        ]}
        avatarAlt={`${displayName}'s avatar`}
        avatarUrl={tenant.avatar_url}
        bio={tenant.bio || 'Morning routes, sweaty logistics, and post-run coffee for the crew.'}
        displayName={displayName}
        initials={displayName.slice(0, 2).toUpperCase()}
        stats={[
          { label: 'Locale', value: tenant.locale },
          { compact: true, label: 'Timezone', value: tenant.timezone },
        ]}
        subtitle={tenant.slug}
        topPadding={32}
      >
        <UpcomingEvents error={eventsError} eventHref={eventHref} events={events} tenant={tenant} />
      </ProfileScreen>
    </>
  );
}
